<template>
  <div class="table-container scrollable">
    <table class="table is-fullwidth">
      <thead>
        <tr>
          <th class="sticky-row">
            <b-button
              :type="isAllOn ? 'active is-dark' : ''"
              class="has-text-weight-bold"
              :class="{ 'matrix-all-button': !isAllOn }"
              :loading="toggling"
              @click="toggleAll"
              v-if="rows.length && columns.length"
            >
              ALL
            </b-button>
          </th>
          <th
            class="sticky-row has-text-centered"
            v-for="(code, column) in columns"
            :key="code.id"
          >
            <matrix-column
              :code="code"
              :is-on="isColumnOn(column)"
              :toggle="() => toggleColumn(column)"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(code, row) in rows" :key="code.id">
          <th class="sticky-col">
            <matrix-row
              :code="code"
              :is-on="isRowOn(row)"
              :colors="colors"
              :toggle="() => toggleRow(row)"
            />
          </th>
          <td
            v-for="(association, col) in associations[row]"
            :key="col"
            class="has-text-centered"
          >
            <matrix-association
              :association="association"
              :row="rows[row]"
              :column="columns[col]"
              :colors="colors"
              :toggle="() => toggleAssociation(row, col)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import MatrixRow from "./matrixRow";
import MatrixColumn from "./matrixColumn";
import matrixAssociation from "./matrixAssociation";
import { CodeService } from "@/services/code.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import { generateLightColor } from "@/helpers/colors";

export default {
  codeService: CodeService,
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Array,
      default: () => []
    },
    associations: {
      type: Array,
      default: () => []
    },
    contract: {
      type: String | Number,
    }
  },
  components: {
    MatrixRow,
    MatrixColumn,
    matrixAssociation
  },
  data: () => ({
    colors: {},
    toggling: false
  }),
  computed: {
    columnsIds() {
      return this.columns.map(code => code.id);
    },
    rowsIds() {
      return this.rows.map(code => code.id);
    },
    isAllOn() {
      for (let x = 0; x < this.rows.length; x++) {
        if (!this.isRowOn(x)) {
          return false;
        }
      }

      return true;
    }
  },
  watch: {
    // Calling generateColors again on columns change can affect performance.
    // By the process, when rows changes, columns is also expected to change.
    rows() {
      this.generateColors();
    }
  },
  methods: {
    generateColors() {
      const groups = this.rows.map(code => code.group);

      this.colors = groups.reduce((acc, group) => {
        acc[group] = generateLightColor();
        return acc;
      }, {});
    },
    isColumnOn(col) {
      const associations = [];

      for (let x = 0; x < this.associations.length; x++) {
        associations.push(this.associations[x][col]);
      }

      return associations.every(assoc => assoc);
    },
    isRowOn(row) {
      const associations = [];

      for (let x = 0; x < this.associations[row].length; x++) {
        associations.push(this.associations[row][x]);
      }

      return associations.every(assoc => assoc);
    },
    async toggleColumn(col) {
      const code = this.columns[col].id;

      if (this.isColumnOn(col)) {
        // Turns off columns
        await this.$options.codeService.detachChildren(code, {
          code_ids: this.rowsIds,
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations(
          [...Array(this.rows.length).keys()],
          [col],
          false
        );
      } else {
        // Turns on columns
        await this.$options.codeService.syncChildren(code, {
          code_ids: this.rowsIds,
          detach: false,
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations(
          [...Array(this.rows.length).keys()],
          [col],
          true
        );
      }
    },
    async toggleRow(row, toggleOn = null) {
      const code = this.rows[row].id;

      if (toggleOn !== null ? !toggleOn : this.isRowOn(row)) {
        // Turns off rows
        await this.$options.codeService.detachChildren(code, {
          code_ids: this.columnsIds,
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations(
          [row],
          [...Array(this.columns.length).keys()],
          false
        );
      } else {
        // Turns on rows
        await this.$options.codeService.syncChildren(code, {
          code_ids: this.columnsIds,
          detach: false,
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations(
          [row],
          [...Array(this.columns.length).keys()],
          true
        );
      }
    },
    async toggleAssociation(row, col) {
      const parent = this.columns[col].id;
      const child = this.rows[row].id;

      // Is association on
      if (this.associations[row][col]) {
        // Turn off association
        await this.$options.codeService.detachChildren(parent, {
          code_ids: [child],
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations([row], [col], false);
      } else {
        // Turn on association
        await this.$options.codeService.syncChildren(parent, {
          code_ids: [child],
          detach: false,
          contract_id: this.contract || undefined,
        });
        this.toggleAssociations([row], [col], true);
      }
    },
    toggleAssociations(rows, cols, value) {
      rows.forEach(row => {
        cols.forEach(col => {
          this.$set(
            this.associations[row],
            col,
            value || !this.associations[row][col]
          );
        });
      });
    },
    async toggleAll() {
      const toggles = this.rows.map((row, index) => this.toggleRow(index, !this.isAllOn));

      this.toggling = true;

      try {
        await Promise.all(toggles);

        this.$buefy.toast.open({
          message: "Codes has been associated.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}

      this.toggling = false;
    }
  }
};
</script>

<style scoped>
.scrollable {
  height: calc(100vh - 6.5rem - 1.5rem - 1rem - 1.5rem - 75.58px);
}
.table-container {
  /* margin-left: 5em; */
}
.sticky-row,
.sticky-col {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  left: 0;
  background: white;
  z-index: 1;
}
.matrix-all-button {
  border-left: 2px solid #363636;
}
.matrix-all-button:hover {
  background-color: #363636;
  border-color: #363636;
  color: whitesmoke;
}
.matrix-all-button[disabled]:hover {
  border-color: inherit;
  background-color: inherit;
  color: inherit;
  border-left: 2px solid #363636;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/CodesMatrixManager/partials/matrix.vue